import { ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Chip, Collapse, Grid, Icon, IconButton } from "@mui/material";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";
import ComplexStatisticsCard from "components/Cards/StatisticsCards/ComplexStatisticsCard";
import MDBox from "mdComponents/MDBox";
import MDButton from "mdComponents/MDButton";
import MDTypography from "mdComponents/MDTypography";
import { useUser } from "context/UserContext";
import { useNavigationMessageContext } from "context/NavigationMessageContext";
import corporateAccountService from "services/corporateAccountService";
import { CorporateAccountROI } from "../../models/corporateAccountROI";
import { CorporateAccountTotalVisits } from "../../models/corporateAccountTotalVisitsModel";
import { CorporateHostRating } from "../../models/corporateHostRatingModel";
import { CorporatePoorConditionReportCount } from "../../models/corporatePoorConditionReportCountModel";
import { CorporateDoorLockIssueCount } from "../../models/corporateDoorLockIssueCountModel";
import { CorporateHostStatusRollup } from "../../models/corporateHostStatusRollupModel";
import { formatDate, formatPercent } from "Utils";
import CorporateVisits from "controls/CorporateVisits";
import CorporateROI from "controls/CorporateROI";
import CorporateRatings from "controls/CorporteRatings";
import CorporateHostPerformance from "controls/CorporatePerformance";
import CorporateDoorLockIssues from "controls/CorporateDoorLockIssues";
import CorporatePoorConditionReports from "controls/CorporatePoorConditionReportCount";

type DetailKey = "visits" | "locations" | "roi" | "ratings" | "locks" | "reports";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, userCorporateAccount } = useUser();
  const { setMessage } = useNavigationMessageContext();
  const [activeDetail, setActiveDetail] = useState<DetailKey | null>(null);
  const [rollupROI, setRollupROI] = useState<CorporateAccountROI>();
  const [rollupVisit, setRollupVisit] = useState<CorporateAccountTotalVisits>();
  const [rollupRating, setRollupRating] = useState<CorporateHostRating>();
  const [rollupReport, setRollupReport] = useState<CorporatePoorConditionReportCount>();
  const [rollupLockIssue, setRollupLockIssue] = useState<CorporateDoorLockIssueCount>();
  const [rollupStatus, setRollupStatus] = useState<CorporateHostStatusRollup>();

  useEffect(() => {
    setMessage(`${userCorporateAccount?.businessName ?? "Business"} Dashboard`);
    return () => setMessage(undefined);
  }, [setMessage, userCorporateAccount]);

  useEffect(() => {
    if (!userCorporateAccount?.id) return;
    const id = userCorporateAccount.id;

    Promise.all([
      corporateAccountService.getCorporateROI(id),
      corporateAccountService.getCorporateVisits(id),
      corporateAccountService.getCorporateHostRatings(id),
      corporateAccountService.getCorporateHostPoorConditionReportsCounts(id),
      corporateAccountService.getCorporateHostDoorLockIssueCounts(id),
      corporateAccountService.getCorporateHostStatusRollup(id),
    ])
      .then(([roi, visits, ratings, reports, locks, status]) => {
        setRollupROI(roi?.find((item) => item.host_id === -1));
        setRollupVisit(visits?.find((item) => item.hostId === -1));
        setRollupRating(ratings?.find((item) => item.hostId === -1));
        setRollupReport(reports?.find((item) => item.hostId === -1));
        setRollupLockIssue(locks?.find((item) => item.hostId === -1));
        setRollupStatus(status);
      })
      .catch((error) => console.error("Unable to load dashboard metrics", error));
  }, [userCorporateAccount?.id]);

  const attentionItems = useMemo(
    () => [
      {
        key: "locations" as DetailKey,
        label: "Awaiting approval",
        count: rollupStatus?.waitingApproval ?? 0,
        icon: "schedule",
        tone: "warning",
      },
      {
        key: "locks" as DetailKey,
        label: "Door-lock issues",
        count: rollupLockIssue?.totalIssues ?? 0,
        icon: "key",
        tone: "error",
      },
      {
        key: "reports" as DetailKey,
        label: "Condition reports",
        count: rollupReport?.totalReports ?? 0,
        icon: "report_problem",
        tone: "error",
      },
      {
        key: "locations" as DetailKey,
        label: "Closed for cleaning",
        count: rollupStatus?.closedForCleaning ?? 0,
        icon: "cleaning_services",
        tone: "warning",
      },
    ],
    [rollupLockIssue, rollupReport, rollupStatus]
  );

  const totalAttention = attentionItems.reduce((total, item) => total + item.count, 0);
  const toggleDetail = (detail: DetailKey) =>
    setActiveDetail((current) => (current === detail ? null : detail));

  const details: Record<DetailKey, { title: string; component: ReactNode }> = {
    visits: { title: "Visit performance", component: <CorporateVisits /> },
    locations: { title: "Location health", component: <CorporateHostPerformance /> },
    roi: { title: "Return on investment", component: <CorporateROI /> },
    ratings: { title: "Customer ratings", component: <CorporateRatings /> },
    locks: { title: "Door-lock issues", component: <CorporateDoorLockIssues /> },
    reports: { title: "Poor-condition reports", component: <CorporatePoorConditionReports /> },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={{ xs: 2.5, md: 4 }}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          flexDirection={{ xs: "column", md: "row" }}
          gap={2}
          mb={3.5}
        >
          <MDBox>
            <MDTypography variant="h3" fontWeight="bold" sx={{ letterSpacing: "-0.035em" }}>
              {user?.firstName ? `Good to see you, ${user.firstName}` : "Business overview"}
            </MDTypography>
            <MDTypography variant="body2" color="text" mt={0.5}>
              Here&apos;s how {userCorporateAccount?.businessName ?? "your business"} is performing
              today.
            </MDTypography>
          </MDBox>
          <MDButton color="info" variant="contained" onClick={() => navigate("/location/add")}>
            <Icon sx={{ mr: 0.75 }}>add</Icon>
            Add location
          </MDButton>
        </MDBox>

        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={12} sm={6} lg={3}>
            <ComplexStatisticsCard
              title="Visits this week"
              count={rollupVisit?.visitsThisWeek}
              loading={!rollupVisit}
              icon="leaderboard"
              selected={activeDetail === "visits"}
              percentage={{
                amount: rollupVisit?.totalVisits,
                label: "all-time visits",
                color: "info",
              }}
              onClick={() => toggleDetail("visits")}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <ComplexStatisticsCard
              title="Open locations"
              count={rollupStatus?.currentlyOpen}
              loading={!rollupStatus}
              color="dark"
              icon="location_on"
              selected={activeDetail === "locations"}
              percentage={{
                amount: rollupStatus
                  ? `${rollupStatus.approved}/${rollupStatus.totalHosts}`
                  : undefined,
                label: "approved locations",
                color: "info",
              }}
              onClick={() => toggleDetail("locations")}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <ComplexStatisticsCard
              title="Monthly ROI"
              count={formatPercent(rollupROI?.roi_month)}
              loading={!rollupROI}
              color="success"
              icon="trending_up"
              selected={activeDetail === "roi"}
              percentage={{
                amount: formatPercent(rollupROI?.roi_lifetime),
                label: "lifetime return",
                color: "success",
              }}
              onClick={() => toggleDetail("roi")}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <ComplexStatisticsCard
              title="Average rating"
              count={rollupRating?.avgRating?.toFixed(1)}
              loading={!rollupRating}
              color="warning"
              icon="star"
              selected={activeDetail === "ratings"}
              percentage={{
                amount: rollupRating?.totalRatings,
                label: "customer ratings",
                color: "warning",
              }}
              onClick={() => toggleDetail("ratings")}
            />
          </Grid>
        </Grid>

        <Card sx={{ p: { xs: 2.25, md: 2.75 }, mb: 3 }}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2.25}>
            <MDBox display="flex" alignItems="center" gap={1.25}>
              <MDBox
                width={38}
                height={38}
                borderRadius="lg"
                display="grid"
                sx={{ placeItems: "center", bgcolor: totalAttention ? "#fff7ed" : "#f0fdf4" }}
              >
                <Icon
                  sx={{ color: totalAttention ? "warning.main" : "success.main", fontSize: 21 }}
                >
                  {totalAttention ? "notifications_active" : "check_circle"}
                </Icon>
              </MDBox>
              <MDBox>
                <MDTypography variant="h6" fontWeight="bold">
                  {totalAttention ? "Needs attention" : "Everything looks healthy"}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {totalAttention
                    ? `${totalAttention} operational items across your locations`
                    : "No active operational issues"}
                </MDTypography>
              </MDBox>
            </MDBox>
            <Chip
              label={totalAttention ? `${totalAttention} open` : "All clear"}
              size="small"
              color={totalAttention ? "warning" : "success"}
              variant="outlined"
            />
          </MDBox>

          <Grid container spacing={1.5}>
            {attentionItems.map((item, index) => (
              <Grid item xs={12} sm={6} lg={3} key={`${item.label}-${index}`}>
                <MDBox
                  onClick={() => toggleDetail(item.key)}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  px={1.75}
                  py={1.5}
                  borderRadius="lg"
                  sx={{
                    cursor: "pointer",
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: item.count ? "#fffbeb" : "grey.100",
                    "&:hover": { borderColor: "info.main", bgcolor: "#f8fafc" },
                  }}
                >
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Icon
                      sx={{
                        color: item.count ? `${item.tone}.main` : "text.secondary",
                        fontSize: 19,
                      }}
                    >
                      {item.icon}
                    </Icon>
                    <MDTypography variant="caption" fontWeight="medium">
                      {item.label}
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="h6" fontWeight="bold">
                    {item.count}
                  </MDTypography>
                </MDBox>
              </Grid>
            ))}
          </Grid>
        </Card>

        <Collapse in={Boolean(activeDetail)} unmountOnExit>
          {activeDetail && (
            <Card sx={{ p: { xs: 2.25, md: 3 }, mb: 3 }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDBox>
                  <MDTypography variant="overline" color="info" fontWeight="bold">
                    Performance detail
                  </MDTypography>
                  <MDTypography variant="h5" fontWeight="bold">
                    {details[activeDetail].title}
                  </MDTypography>
                </MDBox>
                <IconButton onClick={() => setActiveDetail(null)} aria-label="Close detail">
                  <Icon>close</Icon>
                </IconButton>
              </MDBox>
              {details[activeDetail].component}
            </Card>
          )}
        </Collapse>

        <MDBox display="flex" justifyContent="space-between" alignItems="center" px={0.5}>
          <MDTypography variant="caption" color="text">
            Metrics reflect activity across all approved locations.
          </MDTypography>
          {(rollupLockIssue?.lastReportedAt || rollupReport?.lastReportedAt) && (
            <MDTypography variant="caption" color="text">
              Latest issue:{" "}
              {formatDate(rollupLockIssue?.lastReportedAt || rollupReport?.lastReportedAt, true)}
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default Dashboard;
